import React from 'react';
import { useSelector } from 'react-redux';
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  Tooltip,
  IconButton,
  Fab,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  REPORT_TYPE_OPTIONS,
  SR_SUBTYPE_OPTIONS,
  SC_SUBTYPE_OPTIONS,
  MODE_OPTIONS,
  TAG_MODALITY_OPTIONS,
  EMPTY_RULE,
  REPORT_ITEMS,
  VENDORS,
  ruleFlagOn,
} from '../Interfaces.helpers';
import SR_Report from '../../../../../../resources/assets/SR_Report.png';
import QualityCoreReport from '../../../../../../resources/assets/quality_core.png';
import QualityDetailedReport from '../../../../../../resources/assets/quality_detailed.png';
import DiagnosticsReport from '../../../../../../resources/assets/diagnostics.png';
import ScoreCardReport from '../../../../../../resources/assets/scoreCard.jpeg';

const IMAGE_MAP = {
  SR_Report,
  ScoreCardReport,
  DiagnosticsReport,
  QualityDetailedReport,
  QualityCoreReport,
};

const SendingRulesEditor = ({ form, updateForm, initialData, classes, t }) => {
  const institutionList = useSelector((state) => state.operators_devices.institution_list) || [];
  const typeOptions = REPORT_TYPE_OPTIONS(t);
  const SRsubTypeOptions = SR_SUBTYPE_OPTIONS(t);
  const SCsubTypeOptions = SC_SUBTYPE_OPTIONS(t);
  const modeOptions = MODE_OPTIONS(t);
  const tagModalityOptions = TAG_MODALITY_OPTIONS;
  const reportItems = REPORT_ITEMS(t);

  const updateRule = (index, updates) => {
    const sending_rules = [...(form.sending_rules || [])];
    sending_rules[index] = { ...sending_rules[index], ...updates };
    updateForm('sending_rules', sending_rules);
  };

  const addRule = () => {
    updateForm('sending_rules', [...(form.sending_rules || []), EMPTY_RULE]);
  };

  const removeRule = (index) => {
    const sending_rules = (form.sending_rules || []).filter((_, i) => i !== index);
    updateForm('sending_rules', sending_rules.length > 0 ? sending_rules : [EMPTY_RULE]);
  };

  return (
    <>
      {/* Sending Rules */}
      <div className={classes.modulesContainer}>
        <div className={classes.listHeaderContainer}>
          <Typography className={classes.listHeaderName}>{t('Settings.sending_rules')}</Typography>
          <Tooltip
            classes={{ tooltip: classes.customWidth }}
            title={
              <div className={classes.send_report_main_container}>
                <div className={classes.send_report_img_container}>
                  {reportItems.map((item, idx) => (
                    <div key={idx} className={classes.manual_report_type_container}>
                      <Typography className={classes.report_title}>{item.title}</Typography>
                      <Typography className={classes.report_explanation}>{item.subtitle}</Typography>
                      <div className={classes.img_container}>
                        <img
                          src={IMAGE_MAP[item.image]}
                          alt='report_thumbnail'
                          className={classes.img_style}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            arrow
            placement='top-end'>
            <IconButton className={classes.InfoIcon} size='small'>
              <InfoIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>

        {(form.sending_rules || [EMPTY_RULE]).map((ruleDetail, idx) => (
          <Grid container spacing={1} key={idx} alignItems='center' className={classes.ruleRowContainer}>
            {/* Type */}
            <Grid item xs={2}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('Settings.report_type')}</InputLabel>
                <Select
                  value={ruleDetail?.type || ''}
                  onChange={(e) => updateRule(idx, {
                    type: e.target.value,
                    subtype: '',
                    dark_mode: false,
                    monochrome: false,
                  })}
                  displayEmpty
                  renderValue={(v) => (!v ? t('Settings.report_type') : v)}>
                  {typeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Subtype */}
            <Grid item xs={ruleDetail?.type === 'SC' ? 3 : 4}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('Settings.report_subtype')}</InputLabel>
                <Select
                  value={ruleDetail?.subtype || ''}
                  onChange={(e) => updateRule(idx, { subtype: e.target.value })}
                  displayEmpty
                  renderValue={(v) => (!v ? t('Settings.report_subtype') : t(`Settings.${v}`))}>
                  {ruleDetail?.type === 'SR' &&
                    SRsubTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  {ruleDetail?.type === 'SC' &&
                    SCsubTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  {!ruleDetail?.type && (
                    <MenuItem value='' disabled>
                      {t('Settings.no_type_selected')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Mode */}
            <Grid item xs={ruleDetail?.type === 'SC' ? 2 : 3}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('Settings.report_mode')}</InputLabel>
                <Select
                  value={ruleDetail?.mode || ''}
                  onChange={(e) => updateRule(idx, { mode: e.target.value })}
                  displayEmpty
                  renderValue={(v) => (!v ? t('Settings.report_mode') : t(`Settings.${v}`))}>
                  {modeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {ruleDetail?.type === 'SC' && (
              <Grid item xs={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      color='secondary'
                      checked={ruleFlagOn(ruleDetail?.dark_mode)}
                      onChange={(e) => updateRule(idx, { dark_mode: e.target.checked })}
                    />
                  }
                  label={t('Settings.appearance_dark_mode')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      color='secondary'
                      checked={ruleFlagOn(ruleDetail?.monochrome)}
                      onChange={(e) => updateRule(idx, { monochrome: e.target.checked })}
                    />
                  }
                  label={t('Settings.appearance_monochrome')}
                />
              </Grid>
            )}

            {/* Vendor or Tag */}
            {ruleDetail?.type === 'SR' && (
              <Grid item xs={2} className={classes.gridItemCenter}>
                <FormControl fullWidth>
                  <InputLabel shrink>{t('Settings.vendor')}</InputLabel>
                  <Select
                    value={ruleDetail?.vendor || ''}
                    onChange={(e) => updateRule(idx, { vendor: e.target.value })}
                    displayEmpty>
                    {VENDORS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {ruleDetail?.type === 'SC' && (
              <Grid item xs={2} className={classes.gridItemCenter}>
                <FormControl fullWidth>
                  <InputLabel shrink id='modality-tag-label'>
                    {t('Settings.tag_modality')}
                  </InputLabel>
                  <Select
                    labelId='modality-tag-label'
                    displayEmpty
                    value={ruleDetail?.modality_tag || ''}
                    onChange={(e) => updateRule(idx, { modality_tag: e.target.value })}
                    renderValue={(selected) => (!selected ? <>{t('Settings.tag_modality')}</> : selected)}>
                    {tagModalityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {!ruleDetail?.type && (
              <Grid item xs={2} className={classes.gridItemCenter}>
                <div
                  style={{
                    width: '100%',
                    height: '48px',
                    opacity: 0.5,
                    borderBottom: '1px solid gray',
                  }}
                />
              </Grid>
            )}

            {/* Active Switch */}
            <Grid item xs={1} className={classes.gridItemEnd}>
              {initialData?.sending_rules && (
                <Tooltip
                  title={
                    <Typography variant='body1'>
                      {ruleDetail?.is_active === 1
                        ? t('UserPanel.enable_click')
                        : t('UserPanel.disable_click')}
                    </Typography>
                  }>
                  <Switch
                    size='small'
                    checked={ruleDetail?.is_active === 1}
                    onChange={() => {
                      const newIsActive = ruleDetail?.is_active === 1 ? 0 : 1;
                      updateRule(idx, { is_active: newIsActive });
                    }}
                    color='secondary'
                  />
                </Tooltip>
              )}
              <Tooltip title={<Typography variant='body1'>{t('Settings.remove')}</Typography>} arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => removeRule(idx)}
                    className={classes.iconButtonSize}
                    color='secondary'
                    // disabled={(form.sending_rules || []).length === 1}
                  >
                    <DeleteIcon
                      fontSize='small'
                      color='secondary'
                      className={classes.deleteIcon}
                      // className={`${classes.deleteIcon} ${
                      //   (form.sending_rules || []).length === 1
                      //     ? classes.deleteIconDisabled
                      //     : classes.deleteIconEnabled
                      // }`}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        ))}
        <div className={classes.addRuleFabContainer}>
          <Tooltip title={<Typography variant='body1'>{t('Settings.add_rule')}</Typography>} arrow>
            <Fab size='small' onClick={addRule} className={classes.addRuleFab} color='secondary'>
              <AddIcon fontSize='small' />
            </Fab>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default SendingRulesEditor;
